import React, { useEffect } from 'react';

import { useWhapi, useWhapiGroups } from 'context';
import { Box, Checkbox, IconButton, Tooltip, useTheme } from '@mui/material';
import { TagMemberType } from 'type/type';
import { Gem } from 'lucide-react';

const ListGroup = () => {
  const theme = useTheme();
  const { advanceGroups, setAdvanceGroups, selectedGroups, setSelectedGroups, selectedTags } = useWhapi();
  const whapiGroup = useWhapiGroups() ?? [];
  const lastSelectedGroupRef = React.useRef<HTMLDivElement | null>(null);
  const handleSelectGroup = (member: TagMemberType) => {
    setSelectedGroups(prev => (prev.includes(member) ? prev.filter(item => item !== member) : [...prev, member]));
  };
  const handleSelectAdvanceGroup = (member: TagMemberType) => {
    setAdvanceGroups(currentAdvanceGroups => {
      // Check if the member's group already exists
      const existingGroupIndex = currentAdvanceGroups.findIndex(group => group.name === member.PartitionKey);

      if (existingGroupIndex !== -1) {
        // Group exists, update members within it
        const updatedGroups = [...currentAdvanceGroups];
        const existingMembers = updatedGroups[existingGroupIndex].members;

        if (existingMembers.some(existingMember => existingMember.RowKey === member.RowKey)) {
          // Member exists, remove it
          updatedGroups[existingGroupIndex].members = existingMembers.filter(item => item.RowKey !== member.RowKey);
        } else {
          // Member doesn't exist, add it
          updatedGroups[existingGroupIndex].members = [...existingMembers, member];
        }
        return updatedGroups.filter(group => group.members.length > 0);
      } else {
        // Group doesn't exist, add new group with the member
        return [...currentAdvanceGroups, { name: member.PartitionKey, members: [member] }];
      }
    });
  };
  useEffect(() => {
    setAdvanceGroups(prev =>
      prev
        .map(group => ({
          ...group,
          members: group.members.filter(member => selectedGroups.includes(member)),
        }))
        .filter(group => group.members.length > 0),
    );
  }, [selectedGroups]);
  const isMemberInAdvanceGroups = (member: TagMemberType) => {
    return advanceGroups.some(group => group.name === member.PartitionKey && group.members.some(advanceMember => advanceMember.RowKey === member.RowKey));
  };

  useEffect(() => {
    if (lastSelectedGroupRef.current) lastSelectedGroupRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [selectedGroups, selectedTags]);
  return (
    whapiGroup && (
      <Box sx={{ overflowX: 'hidden', overflowY: 'scroll', height: '100%' }}>
        {whapiGroup.map((tagData, index) => (
          <React.Fragment key={index}>
            {tagData.members.map((member, index) => {
              const lastSelectedGroup = selectedTags[selectedTags.length - 1] === tagData.name && index === 0;
              return (
                <Box key={index} ref={lastSelectedGroup ? lastSelectedGroupRef : undefined} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                  <Checkbox size='small' checked={selectedGroups.includes(member)} onChange={() => handleSelectGroup(member)} />
                  {selectedGroups.includes(member) && (
                    <IconButton sx={{ marginRight: '10px' }} onClick={() => handleSelectAdvanceGroup(member)}>
                      <Gem size={15} style={{ cursor: 'pointer' }} color={isMemberInAdvanceGroups(member) ? 'var(--info)' : theme.palette.mode === 'dark' ? 'var(--white04)' : 'var(--black04)'} />
                    </IconButton>
                  )}
                  <Tooltip title={member.fn} arrow placement={'right'}>
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                      <p
                        style={{ backgroundColor: 'var(--black02)', width: '30px', height: '20px', lineHeight: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 600, borderRadius: '5px' }}
                      >
                        {tagData.name && tagData.name.toLocaleUpperCase()}
                      </p>
                      <p className='text-overflow noselect' style={{ width: '120px' }}>
                        - {member.fn}
                      </p>
                    </div>
                  </Tooltip>
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    )
  );
};

export default ListGroup;
